import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Function to get CSRF token - checks sessionStorage first (for cross-domain), then cookies
const getCSRFToken = (): string | null => {
  // First check sessionStorage (works across domains)
  const sessionToken = sessionStorage.getItem('csrfToken');
  if (sessionToken) {
    return sessionToken;
  }
  
  // Fall back to cookies (for same-domain scenarios)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === '_csrf') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for httpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add CSRF token
api.interceptors.request.use(
  (config) => {
    // Add CSRF token to all non-GET requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Import auth service to refresh CSRF token
let authService: any;
// Lazy import to avoid circular dependency
const getAuthService = () => {
  if (!authService) {
    authService = require('./auth').authService;
  }
  return authService;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Check if response contains a new CSRF token in header
    const newCsrfToken = response.headers['x-csrf-token'];
    if (newCsrfToken) {
      // Token will be set by the browser automatically via Set-Cookie
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle CSRF token errors
    if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_ERROR') {
      // Prevent infinite loop
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          // Refresh CSRF token
          await getAuthService().refreshCSRFToken();
          
          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh CSRF token:', refreshError);
          // If refresh fails, continue with original error
        }
      }
    }
    
    if (error.response?.status === 401) {
      // Only redirect to login if not already on the login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;